import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import httpClient from "../../utils/httpClients";

function Checkout() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const plan = location.state?.plan;

  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState({ amount: 0, type: "" });
  const [loading, setLoading] = useState(false);
  const [paypalError, setPaypalError] = useState("");
  const [demoMode, setDemoMode] = useState(false);
  const [isCouponValid, setIsValidCoupon] = useState(null);
  const [coupons, setCoupons] = useState({});

  const currentPlan = plan;

  // ===============================
  // API CALLS
  // ===============================

  const assignSubscription = async (planId, paymentDetails) => {
    const response = await httpClient.post(
      "/user/user-subscription/assign",
      paymentDetails,
    );
    return response.data;
  };

  const createPayPalOrder = async (finalAmount) => {
    const response = await httpClient.post(
      "/common/paypal/create-order",
      {
        amount: finalAmount,
        currency_code: "USD",
      },
      { skipLoader: true, skipToast: true },
    );
    return response.data.orderId;
  };

  const capturePayPalOrder = async (orderId) => {
    const response = await httpClient.post(
      `/common/paypal/capture-order/${orderId}`,
      { skipLoader: true, skipToast: true },
    );
    return response.data;
  };

  // ===============================
  // PRICE CALCULATION
  // ===============================

  function getFinalPrice(basePrice, discountObj) {
    if (!discountObj || !discountObj.type || discountObj.amount === 0)
      return basePrice;

    if (discountObj.type === "Percent") {
      return Math.max(0, basePrice * (1 - discountObj.amount / 100));
    }

    if (discountObj.type === "flat") {
      return Math.max(0, basePrice - discountObj.amount);
    }

    return basePrice;
  }

  const finalPrice = getFinalPrice(currentPlan?.price || 0, discount);

  // ===============================
  // HANDLE PAYMENT SUCCESS
  // ===============================

  const handlePaymentSuccess = async (orderId, details, method) => {
    try {
      setLoading(true);

      const paymentDetails = {
        planId: currentPlan.id || currentPlan._id,
        paymentMethod: method,
        transactionId: details.id || orderId,
        amount: finalPrice.toFixed(2),
        couponUsed: discount.amount > 0 ? couponCode : null,
        demoMode: demoMode,
      };

      const apiResponse = await assignSubscription(
        currentPlan.id || currentPlan._id,
        paymentDetails,
      );
      console.log("payment success", details);
      //   alert("✅ Payment Successful!");

      sessionStorage.removeItem("checkoutPlan");
      navigate("/payment-success", {
        state: {
          plan: currentPlan,
          orderId,
          payer: details.payer,
          amount: finalPrice.toFixed(2),
          subscriptionData: apiResponse,
        },
      });
    } catch (error) {
      alert("❌ Payment was successful but subscription activation failed.");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // DEMO PAYMENT
  // ===============================

  const handleDemoPayment = async () => {
    await handlePaymentSuccess(
      "demo_order_" + Date.now(),
      {
        id: "demo_txn_" + Date.now(),
        payer: {
          name: { given_name: "Demo User" },
        },
      },
      "paypal",
    );
  };

  // ===============================
  // COUPON LOGIC
  // ===============================
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await httpClient.get("/admin/coupon", {
          skipLoader: true,
          skipToast: true,
        });
        if (res.data.success) {
          const formattedCoupons = res.data.data.reduce((acc, item) => {
            acc[item.couponCode] = {
              amount: item?.couponAmount || item?.plan?.couponAmount,
              type: item?.type,
            };
            return acc;
          }, {});

          setCoupons(formattedCoupons);
        }
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchCoupons();
  }, []);

  // const coupons = {
  //   WELCOME10: { amount: 10, type: "percentage" },
  //   SAVE20: { amount: 20, type: "percentage" },
  //   FLAT5: { amount: 5, type: "flat" },
  // };

  console.log("demo", demoMode);
  const handleCouponApply = () => {
    let code = couponCode.trim().toUpperCase();
    const c = coupons[code];

    if (c) {
      setDiscount(c);
      setIsValidCoupon({
        isValid: true,
        message:
          c.type === "Percent"
            ? `🎉 ${c.amount}% discount applied`
            : `🎉 $${c.amount} off applied`,
      });
    } else {
      setDiscount({ amount: 0, type: "" });
      setIsValidCoupon({
        isValid: false,
        message: "Invalid coupon code!",
      });
    }
  };

  if (!currentPlan) {
    return <div className="container mt-5">No plan selected.</div>;
  }

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to="/" className="text-decoration-none">
                <i className="fas fa-home me-2"></i>Home
              </Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active text-warning">
              <i className="fas fa-credit-card me-2"></i>Payment Options
            </li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page">
            <h1 className="card-title text-capitalize mt-3">
              Complete Your Purchase
            </h1>
            <p className="text-muted">
              Secure payment processed with encryption
            </p>
          </div>
        </div>

        <div className="row">
          {/* Left side - Plan info */}
          <div className="col-lg-4 mb-4">
            <div className="card p-4 bg-dark text-white shadow-lg border-0">
              <div className="card-header bg-gradient-primary border-0">
                <h4 className="text-white mb-0">
                  <i className="fas fa-crown me-2 text-warning"></i>
                  {currentPlan.name} Plan
                </h4>
              </div>
              <div className="card-body">
                <div className="text-center mb-4">
                  <h2 className="display-4 fw-bold text-gradient">
                    ${currentPlan.price}
                    <small className="fs-6 text-muted">/month</small>
                  </h2>
                  {/* Show discount for percentage & flat */}
                  {discount.amount > 0 && (
                    <div className="alert alert-success py-2">
                      <s>${currentPlan.price}</s>
                      <span className="ms-2 fw-bold">
                        ${finalPrice.toFixed(2)}
                      </span>
                      <br />
                      <small>
                        {discount.type === "Percent" && (
                          <span>{discount.amount}% discount applied!</span>
                        )}
                        {discount.type === "flat" && (
                          <span>${discount.amount} off applied!</span>
                        )}
                      </small>
                    </div>
                  )}
                </div>

                <ul className="list-unstyled mb-4">
                  {currentPlan.features &&
                    currentPlan.features.map((f, idx) => (
                      <li key={idx} className="mb-3 d-flex align-items-center">
                        <i className="fas fa-check-circle text-success me-3"></i>
                        <span>
                          {f
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </span>
                      </li>
                    ))}
                </ul>

                <div className="text-center">
                  <button
                    className="btn btn-outline-light btn-lg w-100"
                    onClick={() => navigate(-1)}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Change Plan
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Checkout section */}
          <div className="col-lg-8 mb-3">
            <div className="card p-4 bg-dark text-white shadow-lg border-0">
              {/* Demo Mode Toggle */}
              {/* <div className="mb-4 p-3 bg-warning bg-opacity-10 rounded">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="demoModeSwitch"
                    checked={demoMode}
                    onChange={(e) => setDemoMode(e.target.checked)}
                  />
                  <label
                    className="form-check-label fw-bold text-warning"
                    htmlFor="demoModeSwitch"
                  >
                    <i className="fas fa-flask me-2"></i>
                    Enable Demo Mode (Skip PayPal Login)
                  </label>
                </div>
                <small className="text-muted">
                  {demoMode
                    ? "Demo mode active - No real payment required"
                    : "Real PayPal mode - Actual payment processing"}
                </small>
              </div> */}

              {/* Coupon code */}
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Have a coupon code?
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control bg-secondary border-0 text-white"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button
                    className="btn btn-warning px-4"
                    onClick={handleCouponApply}
                  >
                    <i className="fas fa-tag me-2"></i>
                    Apply
                  </button>
                </div>

                {isCouponValid && (
                  <div
                    className={`${isCouponValid.isValid ? "text-success" : "text-danger"} mt-2`}
                  >
                    <small>{isCouponValid.message}</small>
                  </div>
                )}
                {/* Coupon type info if applied */}
                {/* {discount.amount > 0 && discount.type && (
                  <div className="mt-2">
                    <small className="text-success">
                      Coupon applied:{" "}
                      {discount.type === "Percent"
                        ? `${discount.amount}% off`
                        : `$${discount.amount} off`}
                    </small>
                  </div>
                )} */}
              </div>

              {/* PayPal Section */}
              <div
                style={{
                  minHeight: "150px",
                  background: "#fff",
                  padding: "10px",
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  forceReRender={[finalPrice]}
                  createOrder={async () => {
                    console.log("Creating order...");

                    const response = await httpClient.post(
                      "/common/paypal/create-order",
                      {
                        amount: finalPrice.toFixed(2),
                        currency_code: "USD",
                      },
                      { skipLoader: true },
                    );

                    console.log("Create order response:", response.data);

                    return response.data.data.result.id; // very important
                  }}
                  onApprove={async (data) => {
                    console.log("Approved:", data);

                    const captureDetails = await capturePayPalOrder(
                      data.orderID,
                    );

                    await handlePaymentSuccess(
                      data.orderID,
                      captureDetails,
                      "paypal",
                    );
                  }}
                  onError={(err) => {
                    console.error("PayPal error:", err);
                  }}
                />
              </div>

              {/* Security Notice */}
              <div className="alert alert-dark mt-4 text-center border-0">
                <small>
                  <i className="fas fa-lock me-2 text-success"></i>
                  Your payment information is encrypted and secure. We do not
                  store your payment details.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .text-gradient {
          background: linear-gradient(45deg, #fff, #aaa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .bg-gradient-primary {
          background: linear-gradient(45deg, #343a40, #495057);
        }
        .border-dashed {
          border: 2px dashed #495057 !important;
        }
        .payment-provider {
          padding: 8px 12px;
          background: #2a2e32;
          border-radius: 6px;
          font-size: 1.2rem;
        }
        .upi-app {
          flex: 1;
          min-width: 60px;
        }
        .nav-pills .nav-link {
          background: #2a2e32;
          color: #fff;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        .nav-pills .nav-link.active {
          background: linear-gradient(45deg, #007bff, #0056b3);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
        }
        .nav-pills .nav-link:hover {
          background: #3a3e42;
        }
      `}</style>
    </div>
  );
}

export default Checkout;
